import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Check, X, ArrowLeft, Heart } from "lucide-react";

export default function Compare() {
  const [params] = useSearchParams();
  const ids = params.get("ids") || "";
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ids) { setLoading(false); return; }
    api.get("/compare", { params: { ids } })
      .then((r) => setDeals(r.data))
      .finally(() => setLoading(false));
  }, [ids]);

  const usd = (n) => `$${Math.round(n).toLocaleString()}`;
  const removeId = (id) => {
    const remaining = deals.filter((d) => d.id !== id).map((d) => d.id).join(",");
    window.location.href = remaining ? `/compare?ids=${remaining}` : "/lease-deals";
  };

  if (loading) return <div className="max-w-[1400px] mx-auto px-5 py-20 text-gray-400">Loading…</div>;
  if (!deals.length) return (
    <div className="max-w-[700px] mx-auto px-5 py-24 text-center" data-testid="compare-empty">
      <h2 className="font-display text-3xl mb-3">Nothing to compare yet.</h2>
      <p className="text-gray-400 mb-6">Add deals to compare from the marketplace.</p>
      <Link to="/lease-deals" className="px-6 py-3 rounded-full silver-gradient text-[#050507] font-bold">Browse Lease Deals</Link>
    </div>
  );

  const rows = [
    { label: "Monthly Payment", key: "monthly_payment", fmt: usd, lowerBetter: true },
    { label: "Due at Signing", key: "due_at_signing", fmt: usd, lowerBetter: true },
    { label: "MSRP", key: "msrp", fmt: usd },
    { label: "Lease Term", key: "lease_term_months", fmt: (n) => `${n} months` },
    { label: "Annual Mileage", key: "annual_mileage", fmt: (n) => `${(n/1000).toFixed(0)}k miles` },
    { label: "Savings vs Sticker", key: "savings", fmt: usd, higherBetter: true },
    { label: "Vehicle Type", key: "vehicle_type", fmt: (s) => s?.toUpperCase() },
    { label: "Region", key: "region" },
    { label: "Dealer Source", key: "dealer_source" },
    { label: "Incentives", key: "incentives" },
    { label: "Expires", key: "expiration_date" },
  ];

  // Find best in each row
  const bestId = (row) => {
    if (!row.lowerBetter && !row.higherBetter) return null;
    const sorted = [...deals].sort((a, b) => row.lowerBetter ? a[row.key] - b[row.key] : b[row.key] - a[row.key]);
    return sorted[0].id;
  };

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12" data-testid="compare-page">
      <Link to="/lease-deals" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-6"><ArrowLeft className="w-4 h-4" /> Back</Link>
      <p className="label-mono mb-3">Side-by-Side</p>
      <h1 className="font-display text-4xl md:text-6xl font-light tracking-tighter mb-12">Compare your shortlist.</h1>

      <div className={`grid gap-5 mb-10 grid-cols-1 ${deals.length === 2 ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
        {deals.map((d) => (
          <div key={d.id} className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl overflow-hidden" data-testid={`compare-card-${d.id}`}>
            <div className="aspect-[16/10] bg-[#12121A] relative">
              <img src={d.image_url} alt="" className="w-full h-full object-cover" />
              <button onClick={() => removeId(d.id)} className="absolute top-3 right-3 w-8 h-8 rounded-full glass-strong flex items-center justify-center" aria-label="Remove">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5">
              <div className="label-mono">{d.brand} · {d.year}</div>
              <div className="font-display text-xl mt-1">{d.model}</div>
              <div className="text-xs text-gray-500 mt-1">{d.trim}</div>
              <div className="mt-4 font-display text-3xl font-light">${Math.round(d.monthly_payment)}/mo</div>
              <Link to={`/lease-deals/${d.id}`} className="mt-4 block text-center silver-gradient text-[#050507] py-2.5 rounded-full text-xs font-bold tracking-wide">Get This Deal</Link>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5">
            <tr>
              <th className="text-left p-4 label-mono">Spec</th>
              {deals.map((d) => <th key={d.id} className="text-left p-4 font-display">{d.brand} {d.model}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const best = bestId(row);
              return (
                <tr key={row.key} className="border-t border-white/5">
                  <td className="p-4 text-gray-400 text-xs label-mono">{row.label}</td>
                  {deals.map((d) => (
                    <td key={d.id} className={`p-4 ${best === d.id ? "text-emerald-400 font-bold" : ""}`} data-testid={`compare-cell-${row.key}-${d.id}`}>
                      {best === d.id && <Check className="w-3 h-3 inline mr-1.5" />}
                      {row.fmt ? row.fmt(d[row.key]) : d[row.key]}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
